import React, { useContext, useEffect } from "react"
import { useMutation } from "react-query"

import Button from "src/components/Button"

import { declineContract } from "src/api/Contracts/SignContract"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { SignContractWizardContext } from "../../SignContractWizardContext"
import MarinaHeader from "./MarinaHeader"
import PurchaseSummary from "./PurchaseSummary"
import RequirementsPanel from "./RequirementsPanel"

const AgreementOverview = () => {
  const {
    dispatch,
    quote,
    marina,
    pricingSummary,
    presentmentCurrency,
    state,
    steps,
  } = useContext(SignContractWizardContext)

  const tracker = useTracker()
  const showToast = useToast()

  useEffect(() => {
    if (state.contractDetailsSeen === false) {
      tracker.trackEvent("contracts_v2:agreement_opened", {
        contract_quote_id: quote.id,
        marina_id: marina.id,
        marina_name: marina.name,
      })
    }
  }, [state.contractDetailsSeen, quote, marina, tracker])

  const goToPayment = () => {
    dispatch({ type: "CONTRACT_DETAILS_SEEN" })
    dispatch({ type: "GO_TO_NEXT_STEP" })
  }

  const { mutate: decline, isLoading: declining } = useMutation(
    ["declineContract", quote.encodedId],
    declineContract,
    {
      onSuccess: () => dispatch({ type: "CONTRACT_DECLINED" }),
      onError: () =>
        showToast("We couldn't decline this agreement. Please try again.", {
          type: "error",
        }),
    }
  )

  const handleDecline = () => {
    if (!window.confirm("Are you sure you want to decline this contract?"))
      return

    tracker.trackEvent("contracts_v2:agreement_declined", {
      contract_quote_id: quote.id,
      marina_id: marina.id,
      marina_name: marina.name,
      current_step: steps[state.currentStep - 1].title,
    })

    decline({ quoteId: quote.encodedId })
  }

  return (
    <div className="flex flex-col space-y-8">
      <MarinaHeader
        marina={marina}
        onCall={() =>
          tracker.trackEvent("contracts_v2:marina_called", {
            contract_quote_id: quote.id,
            marina_id: marina.id,
            marina_name: marina.name,
          })
        }
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="flex flex-col space-y-4 lg:col-span-2">
          <PurchaseSummary
            quote={quote}
            pricingSummary={pricingSummary}
            presentmentCurrency={presentmentCurrency}
          />
          <Button variant="primary" fullWidth onClick={goToPayment}>
            Choose your payment method
          </Button>
          <p className="flex items-center justify-center gap-2 text-gray-700">
            <i className="fa-solid fa-shield-check text-teal-600" />
            <span>Your payment information is secure and encrypted.</span>
            <i className="fa-solid fa-lock" />
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          {quote.notes && (
            <div>
              <h4 className="mb-2 mt-0 font-semibold text-gray-700">
                Marina Notes
              </h4>
              <div className="rounded-lg border border-gray-300 px-4 py-6">
                <span>{quote.notes}</span>
              </div>
            </div>
          )}
          <RequirementsPanel
            insuranceRequired={quote.insuranceRequired}
            registrationRequired={quote.registrationRequired}
            acceptedPaymentMethods={quote.acceptedPaymentMethods}
            allowDirectPayment={quote.allowDirectPayment}
          />
          <div>
            <h4 className="mb-2 mt-0 font-semibold text-gray-700">
              No longer interested?
            </h4>
            <div className="rounded-lg border border-gray-300 px-4 py-6">
              <Button
                variant="tertiary"
                fullWidth
                isLoading={declining}
                onClick={handleDecline}
              >
                Decline Agreement
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgreementOverview
